<template>
    <div class="duplicate-review-page public_box">
        <div class="duplicate-review-page__header">
            <div>
                <div class="duplicate-review-page__nav">
                    <el-button size="mini" plain @click="goWorkbench">返回核验工作台</el-button>
                    <el-button size="mini" type="text" @click="goSimilarImageReview">切换到相似图片核验</el-button>
                </div>
                <h2>重复图片复核页面</h2>
                <p>集中判断 confirmed_duplicate_hash_record 里的候选图片，是否值得进入深挖风险页面继续使用</p>
            </div>
            <duplicate-review-filter-bar
                v-model="filters.reviewStatus"
                :only-pending="filters.onlyPending"
                :llm-category-level1="filters.llmCategoryLevel1"
                @input="handleReviewStatusChange"
                @toggle-only-pending="handleOnlyPendingToggle"
                @category-change="handleCategoryChange"
            />
        </div>

        <el-alert
            v-if="errorMessage"
            type="error"
            :closable="false"
            :title="errorMessage"
            class="duplicate-review-page__alert"
        />

        <el-empty
            v-if="!loading && !records.length"
            description="当前无可复核记录"
        />

        <div v-if="records.length" class="duplicate-review-page__batch-bar">
            <div class="duplicate-review-page__batch-summary">
                当前页共 {{ records.length }} 条，可一键批量判断
            </div>
            <div class="duplicate-review-page__batch-actions">
                <el-radio-group
                    v-model="batchDecision"
                    size="mini"
                    class="duplicate-review-page__batch-group"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                </el-radio-group>
                <el-button
                    size="mini"
                    type="primary"
                    :loading="batchSaving"
                    @click="handleBatchSaveConfirm"
                >
                    当前页批量保存
                </el-button>
            </div>
        </div>

        <div v-loading="loading" class="duplicate-review-page__list">
            <duplicate-review-card
                v-for="record in records"
                :key="record.reviewRecordId"
                :record="record"
                :duplicate-cases="resolveRecordDuplicateCases(record)"
                :case-state="ensureCaseState(record)"
                :history-visible="!!historyVisibleMap[record.reviewRecordId]"
                :history-loading="!!historyLoadingMap[record.reviewRecordId]"
                :history-items="historyMap[record.reviewRecordId] || []"
                @open-duplicate-case="handleOpenDuplicateCase(record, $event)"
                @toggle-case-list="handleToggleCaseList(record)"
                @change-case-page="handleCasePageChange(record, $event)"
            >
                <template #actions>
                    <duplicate-review-decision-panel
                        v-model="draftStatusMap[record.reviewRecordId]"
                        :saving="savingRecordId === record.reviewRecordId"
                        :history-visible="!!historyVisibleMap[record.reviewRecordId]"
                        @save="handleSave(record)"
                        @toggle-history="toggleHistory(record)"
                    />
                </template>
            </duplicate-review-card>
        </div>

        <div class="duplicate-review-page__pagination">
            <el-pagination
                background
                layout="prev, pager, next"
                :current-page="filters.pageNo"
                :page-size="filters.pageSize"
                :total="total"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script>
import DuplicateReviewCard from "@/components/medical/DuplicateReviewCard.vue";
import DuplicateReviewDecisionPanel from "@/components/medical/DuplicateReviewDecisionPanel.vue";
import DuplicateReviewFilterBar from "@/components/medical/DuplicateReviewFilterBar.vue";
import { buildDuplicateReviewJumpUrl, normalizeDuplicateCaseItems } from "@/views/calculatorTool/medicalThreeColumn.duplicateCase";
import {
    UNIFIED_RISK_TYPE,
    getUnifiedReviewHistory,
    getUnifiedReviewRecords,
    saveUnifiedReviewBatchDecision,
    saveUnifiedReviewDecision,
} from "@/api/unifiedReviewWorkbench";

export default {
    name: "DuplicateReviewPage",
    inject: {
        injectedInterfaceUrl: {
            from: "interfaceUrl",
            default: "",
        },
    },
    components: {
        DuplicateReviewCard,
        DuplicateReviewDecisionPanel,
        DuplicateReviewFilterBar,
    },
    data() {
        return {
            loading: false,
            savingRecordId: null,
            records: [],
            total: 0,
            errorMessage: "",
            filters: {
                reviewStatus: "pending",
                onlyPending: true,
                llmCategoryLevel1: "",
                pageNo: 1,
                pageSize: 20,
            },
            draftStatusMap: {},
            caseStateMap: {},
            historyMap: {},
            historyVisibleMap: {},
            historyLoadingMap: {},
            batchDecision: "useful",
            batchSaving: false,
            internalInterfaceUrl: "",
        };
    },
    created() {
        this.hydrateFiltersFromRoute();
        this.internalInterfaceUrl = this.getResolvedInterfaceUrl();
        this.fetchRecords();
    },
    methods: {
        getDefaultInterfaceUrl() {
            return "";
        },
        goWorkbench() {
            this.$router.push({ path: "/calculatorTool/reviewWorkbench" });
        },
        goSimilarImageReview() {
            this.$router.push({ path: "/calculatorTool/similarImageReview" });
        },
        getResolvedInterfaceUrl() {
            return this.internalInterfaceUrl || this.injectedInterfaceUrl || this.getDefaultInterfaceUrl();
        },
        hydrateFiltersFromRoute() {
            const query = (this.$route && this.$route.query) || {};
            const onlyPending = query.onlyPending !== "false";
            this.filters.onlyPending = onlyPending;
            this.filters.reviewStatus = onlyPending ? "pending" : query.reviewStatus || "all";
            this.filters.llmCategoryLevel1 = query.llmCategoryLevel1 || "";
            this.filters.pageNo = Number(query.pageNo) > 0 ? Number(query.pageNo) : 1;
        },
        syncRouteQuery() {
            if (!this.$router || !this.$route) {
                return;
            }
            this.$router.replace({
                query: {
                    onlyPending: this.filters.onlyPending ? "true" : "false",
                    reviewStatus: this.filters.reviewStatus,
                    llmCategoryLevel1: this.filters.llmCategoryLevel1 || undefined,
                    pageNo: this.filters.pageNo > 1 ? String(this.filters.pageNo) : undefined,
                },
            }).catch(() => {});
        },
        async fetchRecords() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await getUnifiedReviewRecords(
                    this.getResolvedInterfaceUrl(),
                    {
                        riskType: UNIFIED_RISK_TYPE.DUPLICATE_REVIEW,
                        reviewStatus: "all",
                        reviewDecision: this.filters.onlyPending ? "pending" : this.filters.reviewStatus,
                        pageNo: this.filters.pageNo,
                        pageSize: this.filters.pageSize,
                        extFilter1: this.filters.llmCategoryLevel1 || "",
                    },
                    this.instance,
                    (item) => ({
                        ...item,
                        sourceHash: item?.bizKey || item?.detailPayload?.sourceHash || "",
                        md5Hash: item?.detailPayload?.md5Hash || "",
                        sourceCaseNo: item?.primaryCaseNo || item?.detailPayload?.sourceCaseNo || item?.bizKey || "",
                        targetCaseNo: item?.detailPayload?.targetCaseNo || "",
                        leftImageUrl: item?.detailPayload?.leftImageUrl || item?.detailPayload?.sourceImageUrl || "",
                        rightImageUrl: item?.detailPayload?.rightImageUrl || item?.detailPayload?.targetImageUrl || "",
                        leftMaterialLabel: item?.detailPayload?.leftMaterialLabel || item?.detailPayload?.llmCategoryLevel1 || item?.extStr1 || "",
                        rightMaterialLabel: item?.detailPayload?.rightMaterialLabel || item?.detailPayload?.llmCategoryLevel2 || "",
                        contextSummary: item?.detailPayload?.contextSummary || item?.summary || "",
                        llmCategoryLevel1: item?.detailPayload?.llmCategoryLevel1 || item?.extStr1 || "",
                        duplicateCaseCount: Number(item?.extNum1 || item?.detailPayload?.duplicateCaseCount || 0),
                        duplicateCases: normalizeDuplicateCaseItems(item?.detailPayload?.duplicateCases || []),
                        duplicateCaseLoadFailed: false,
                        duplicateCaseLoadMessage: "",
                        reviewStatus: item?.reviewDecision || item?.reviewStatus || "pending",
                        recordStatus: item?.recordStatus || "active",
                        reviewUpdatedAt: item?.reviewedAt || item?.updatedAt,
                        updatedBy: item?.reviewerName || item?.reviewerId || "",
                    })
                );
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "复核记录加载失败");
                }
                const payload = root.data || {};
                this.records = (Array.isArray(payload.items) ? payload.items : []).map((item) => ({
                    ...item,
                    sourceHash: item?.sourceHash || item?.source_hash || item?.sourceCaseNo || "",
                    md5Hash: item?.md5Hash || item?.md5_hash || item?.targetCaseNo || "",
                    duplicateCases: normalizeDuplicateCaseItems(item?.duplicateCases || item?.duplicate_cases || []),
                }));
                this.total = payload.total || 0;
                this.draftStatusMap = this.records.reduce((acc, item) => {
                    acc[item.reviewRecordId] = item.reviewStatus || "pending";
                    return acc;
                }, {});
                this.caseStateMap = this.records.reduce((acc, item) => {
                    const previewCases = normalizeDuplicateCaseItems(item?.duplicateCases || []);
                    const total = Number(item?.duplicateCaseCount || previewCases.length || 0) || 0;
                    acc[item.reviewRecordId] = {
                        expanded: false,
                        loading: false,
                        error: item?.duplicateCaseLoadFailed ? (item?.duplicateCaseLoadMessage || "关联案件加载失败") : "",
                        items: previewCases,
                        pageNo: 1,
                        pageSize: 20,
                        total,
                    };
                    return acc;
                }, {});
                this.resetHistoryState(this.records.map((item) => item.reviewRecordId));
            } catch (error) {
                this.records = [];
                this.total = 0;
                this.caseStateMap = {};
                this.historyMap = {};
                this.historyVisibleMap = {};
                this.historyLoadingMap = {};
                this.errorMessage = error && error.message ? error.message : "复核记录加载失败";
            } finally {
                this.loading = false;
            }
        },
        resetHistoryState(reviewRecordIds = []) {
            reviewRecordIds.forEach((reviewRecordId) => {
                this.$delete(this.historyMap, reviewRecordId);
                this.$delete(this.historyVisibleMap, reviewRecordId);
                this.$delete(this.historyLoadingMap, reviewRecordId);
            });
        },
        ensureCaseState(record) {
            if (!record?.reviewRecordId) {
                return {
                    expanded: false,
                    loading: false,
                    error: "",
                    items: [],
                    pageNo: 1,
                    pageSize: 20,
                    total: 0,
                };
            }
            if (!this.caseStateMap[record.reviewRecordId]) {
                const previewCases = normalizeDuplicateCaseItems(record?.duplicateCases || []);
                this.$set(this.caseStateMap, record.reviewRecordId, {
                    expanded: false,
                    loading: false,
                    error: record?.duplicateCaseLoadFailed ? (record?.duplicateCaseLoadMessage || "关联案件加载失败") : "",
                    items: previewCases,
                    pageNo: 1,
                    pageSize: 20,
                    total: Number(record?.duplicateCaseCount || previewCases.length || 0) || 0,
                });
            }
            return this.caseStateMap[record.reviewRecordId];
        },
        resolveRiskEventId(record) {
            const raw = String(record?.riskEventId || "").trim();
            return /^\d+$/.test(raw) ? raw : "";
        },
        resolveRecordDuplicateCases(record) {
            const state = this.ensureCaseState(record);
            return Array.isArray(state.items) ? state.items : [];
        },
        async fetchDuplicateCases(record, pageNo = 1) {
            const state = this.ensureCaseState(record);
            state.loading = true;
            state.error = "";
            try {
                const items = normalizeDuplicateCaseItems(record?.duplicateCases || []);
                state.items = items;
                state.pageNo = Number(pageNo || 1) || 1;
                state.pageSize = Number(state.pageSize || 20) || 20;
                state.total = Number(record?.duplicateCaseCount || items.length || 0) || 0;
                const riskEventId = this.resolveRiskEventId(record);
                if (!riskEventId) {
                    throw new Error("核验记录ID缺失");
                }
                const response = await getUnifiedReviewHistory(this.getResolvedInterfaceUrl(), riskEventId, this.instance);
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "历史加载失败");
                }
            } catch (error) {
                state.error = error && error.message ? error.message : "关联案件加载失败";
            } finally {
                state.loading = false;
                this.$set(this.caseStateMap, record.reviewRecordId, { ...state });
            }
        },
        async handleToggleCaseList(record) {
            const state = this.ensureCaseState(record);
            if (state.expanded) {
                state.expanded = false;
                this.$set(this.caseStateMap, record.reviewRecordId, { ...state });
                return;
            }
            state.expanded = true;
            this.$set(this.caseStateMap, record.reviewRecordId, { ...state });
            const currentCount = Array.isArray(state.items) ? state.items.length : 0;
            const total = Number(state.total || record?.duplicateCaseCount || 0) || 0;
            if (state.error || total > currentCount) {
                await this.fetchDuplicateCases(record, 1);
            }
        },
        async handleCasePageChange(record, pageNo) {
            const nextPageNo = Number(pageNo) || 1;
            await this.fetchDuplicateCases(record, nextPageNo);
            if (typeof window !== "undefined") {
                window.scrollTo({ top: Math.max(window.scrollY - 120, 0), behavior: "smooth" });
            }
        },
        handleFilterChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleReviewStatusChange(value) {
            this.filters.reviewStatus = value;
            this.handleFilterChange();
        },
        handleOnlyPendingToggle(value) {
            this.filters.onlyPending = value;
            this.filters.reviewStatus = value ? "pending" : "all";
            this.handleFilterChange();
        },
        handleCategoryChange(value) {
            this.filters.llmCategoryLevel1 = value;
            this.handleFilterChange();
        },
        handleBatchSaveConfirm() {
            if (!this.records.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decisionLabelMap = {
                pending: "待定",
                useful: "有用",
                "not-useful": "无用",
            };
            const decisionLabel = decisionLabelMap[this.batchDecision] || this.batchDecision || "待定";
            this.$confirm(
                `确认将当前页 ${this.records.length} 条记录统一标记为「${decisionLabel}」吗？`,
                "批量保存确认",
                {
                    confirmButtonText: "确认保存",
                    cancelButtonText: "取消",
                    type: "warning",
                }
            )
                .then(() => this.handleBatchSave())
                .catch(() => {});
        },
        handlePageChange(pageNo) {
            this.filters.pageNo = pageNo;
            this.records = [];
            this.draftStatusMap = {};
            this.historyMap = {};
            this.historyVisibleMap = {};
            this.historyLoadingMap = {};
            this.syncRouteQuery();
            if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
            this.fetchRecords();
        },
        async handleSave(record) {
            const riskEventId = this.resolveRiskEventId(record);
            if (!riskEventId) {
                this.$message.error("核验记录ID缺失");
                return;
            }
            const decision = this.draftStatusMap[record.reviewRecordId] || "pending";
            this.savingRecordId = record.reviewRecordId;
            try {
                const response = await saveUnifiedReviewDecision(
                    this.getResolvedInterfaceUrl(),
                    riskEventId,
                    { decision },
                    this.instance
                );
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "保存失败");
                }
                const latest = root.data;
                this.draftStatusMap = {
                    ...this.draftStatusMap,
                    [record.reviewRecordId]: decision,
                };
                const normalizedRecords = this.records.map((item) =>
                    item.reviewRecordId === record.reviewRecordId
                        ? {
                            ...item,
                            ...(latest || {}),
                            sourceHash: latest?.sourceHash || latest?.source_hash || item?.sourceHash || item?.sourceCaseNo || "",
                            md5Hash: latest?.md5Hash || latest?.md5_hash || item?.md5Hash || item?.targetCaseNo || "",
                            duplicateCases: normalizeDuplicateCaseItems(
                                latest?.duplicateCases ||
                                latest?.duplicate_cases ||
                                item?.duplicateCases ||
                                []
                            ),
                            reviewStatus: decision,
                        }
                        : item
                );
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = normalizedRecords.filter(
                        (item) => item.reviewRecordId !== record.reviewRecordId
                    );
                    this.total = Math.max(0, Number(this.total || 0) - 1);
                    if (!this.records.length && this.total > 0) {
                        await this.fetchRecords();
                    }
                } else {
                    this.records = normalizedRecords;
                }
                this.$message.success("判断结果已保存");
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "保存失败");
            } finally {
                this.savingRecordId = null;
            }
        },
        async toggleHistory(record) {
            const reviewRecordId = record && record.reviewRecordId;
            if (!reviewRecordId) {
                this.$message.warning("核验记录ID缺失");
                return;
            }
            const visible = Boolean(this.historyVisibleMap[reviewRecordId]);
            this.$set(this.historyVisibleMap, reviewRecordId, !visible);
            if (
                visible ||
                Object.prototype.hasOwnProperty.call(this.historyMap, reviewRecordId)
            ) {
                return;
            }
            const riskEventId = this.resolveRiskEventId(record);
            if (!riskEventId) {
                this.$set(this.historyMap, reviewRecordId, []);
                return;
            }
            this.$set(this.historyLoadingMap, reviewRecordId, true);
            try {
                const response = await getUnifiedReviewHistory(this.getResolvedInterfaceUrl(), riskEventId, this.instance);
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "历史加载失败");
                }
                this.$set(this.historyMap, reviewRecordId, Array.isArray(root.data) ? root.data : []);
            } catch (error) {
                this.$set(this.historyMap, reviewRecordId, []);
                this.$message.error(error && error.message ? error.message : "历史加载失败");
            } finally {
                this.$set(this.historyLoadingMap, reviewRecordId, false);
            }
        },
        async handleBatchSave() {
            const riskEventIds = this.records
                .map((item) => this.resolveRiskEventId(item))
                .filter((item) => Boolean(item));
            if (!riskEventIds.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decision = this.batchDecision || "pending";
            this.batchSaving = true;
            try {
                const response = await saveUnifiedReviewBatchDecision(
                    this.getResolvedInterfaceUrl(),
                    {
                        riskType: UNIFIED_RISK_TYPE.DUPLICATE_REVIEW,
                        riskEventIds,
                        decision,
                    },
                    this.instance
                );
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "批量保存失败");
                }
                this.draftStatusMap = this.records.reduce((acc, item) => {
                    if (riskEventIds.includes(this.resolveRiskEventId(item))) {
                        acc[item.reviewRecordId] = decision;
                    }
                    return acc;
                }, { ...this.draftStatusMap });
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = [];
                    this.total = Math.max(0, Number(this.total || 0) - riskEventIds.length);
                    await this.fetchRecords();
                } else {
                    this.records = this.records.map((item) => ({
                        ...item,
                        reviewStatus: riskEventIds.includes(this.resolveRiskEventId(item)) ? decision : item.reviewStatus,
                    }));
                }
                const updatedCount = Number(root?.data?.updatedCount || riskEventIds.length) || riskEventIds.length;
                this.$message.success(`已批量保存 ${updatedCount} 条判断结果`);
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "批量保存失败");
            } finally {
                this.batchSaving = false;
            }
        },
        handleOpenDuplicateCase(record, item) {
            if (!item?.openable) {
                this.$message.warning(item?.disabledReason || "当前案件暂不可跳转");
                return;
            }
            const nextUrl = buildDuplicateReviewJumpUrl({
                caseNo: item.targetCaseNo,
                focusMaterialId: item.targetMaterialId,
                focusFileHash: item.targetFileHash || record.md5Hash || record.targetCaseNo || record.sourceHash || record.sourceCaseNo,
                focusMaterialType: item.targetMaterialType
            });
            window.open(nextUrl, "_blank");
        },
    },
};
</script>

<style scoped>
.duplicate-review-page {
    padding: 20px;
}

.duplicate-review-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}

.duplicate-review-page__header h2 {
    margin: 0;
    color: #1f2d3d;
}

.duplicate-review-page__nav {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.duplicate-review-page__header p {
    margin: 8px 0 0;
    color: #606266;
    max-width: 640px;
    line-height: 1.6;
}

.duplicate-review-page__alert {
    margin-bottom: 16px;
}

.duplicate-review-page__batch-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    margin-bottom: 16px;
    background: #f7f9fc;
    border: 1px solid #e4eaf3;
    border-radius: 10px;
}

.duplicate-review-page__batch-summary {
    color: #4f5d75;
    font-size: 13px;
}

.duplicate-review-page__batch-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.duplicate-review-page__batch-group {
    flex-shrink: 0;
}

.duplicate-review-page__pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
</style>
